const VARIABLE = /\[[^[\]]*\]/;
const TOKEN = new RegExp(`${VARIABLE.source}|\\d+(?:[.,]\\d+)?|[-+*/()]`, 'g');

// The parser recurses once per bracket, so deeper nesting would overflow the call stack.
const MAX_BRACKET_DEPTH = 50;

export const BUDGET_VARIABLE = 'budget';

export type FormulaVariable = { id: string; name: string };

// null means the formula is malformed.
const calculate = (formula: string, lookup: (variable: string) => number | null): number | null => {
  if (formula.replace(TOKEN, '').trim() !== '') return null;
  const tokens = formula.match(TOKEN) ?? [];
  let position = 0;
  let depth = 0;

  const operand = (): number | null => {
    const token = tokens[position++];
    if (token === undefined) return null;
    if (token === '(') {
      if (++depth > MAX_BRACKET_DEPTH) return null;
      const value = sum();
      depth--;
      return tokens[position++] === ')' ? value : null;
    }
    if (token.startsWith('[')) return lookup(token.slice(1, -1));
    if (/^\d/.test(token)) return Number(token.replace(',', '.'));
    return null;
  };

  const product = (): number | null => {
    let value = operand();
    while (value !== null && (tokens[position] === '*' || tokens[position] === '/')) {
      const operator = tokens[position++];
      const right = operand();
      if (right === null) return null;
      value = operator === '*' ? value * right : value / right;
    }
    return value;
  };

  const sum = (): number | null => {
    let value = product();
    while (value !== null && (tokens[position] === '+' || tokens[position] === '-')) {
      const operator = tokens[position++];
      const right = product();
      if (right === null) return null;
      value = operator === '+' ? value + right : value - right;
    }
    return value;
  };

  const value = sum();
  return position === tokens.length ? value : null;
};

export const isValidFormula = (formula: string, variableNames: string[]): boolean =>
  calculate(formula, variable => (variableNames.includes(variable) ? 0 : null)) !== null;

export const evaluateFormula = (formula: string, valueOf: (variable: string) => number | undefined) => {
  const value = calculate(formula, variable => valueOf(variable) ?? NaN);
  return value !== null && Number.isFinite(value) ? value : undefined;
};

export const formulaVariables = (formula: string) =>
  Array.from(new Set((formula.match(new RegExp(VARIABLE.source, 'g')) ?? []).map(match => match.slice(1, -1))));

const replaceVariables = (formula: string, replace: (variable: string) => string | undefined) =>
  formula.replace(new RegExp(VARIABLE.source, 'g'), match => {
    const variable = match.slice(1, -1);
    return `[${replace(variable) ?? variable}]`;
  });

// Formulas are edited by name and stored by id, which only round-trips while every name is unique.
export const withUniqueNames = <T extends FormulaVariable>(variables: T[]): T[] => {
  const used = new Set<string>();
  const isTaken = (name: string) => used.has(name) || variables.some(variable => variable.name === name);
  return variables.map(variable => {
    if (!used.has(variable.name)) {
      used.add(variable.name);
      return variable;
    }
    let n = 2;
    while (isTaken(`${variable.name} (${n})`)) n++;
    const name = `${variable.name} (${n})`;
    used.add(name);
    return { ...variable, name };
  });
};

export const toStoredFormula = (formula: string, variables: FormulaVariable[]) =>
  replaceVariables(formula, name => variables.find(variable => variable.name === name)?.id);

export const toDisplayFormula = (formula: string, variables: FormulaVariable[]) =>
  replaceVariables(formula, id => variables.find(variable => variable.id === id)?.name);

const ENDS_WITH_OPERAND = /[\])\d]\s*$/;

export const insertVariable = (formula: string, variableName: string, selectionStart: number, selectionEnd: number) => {
  const before = formula.slice(0, selectionStart);
  const variable = `[${variableName}]`;
  const upToCaret = ENDS_WITH_OPERAND.test(before) ? `${before.trimEnd()} + ${variable}` : before + variable;
  return {
    formula: upToCaret + formula.slice(selectionEnd),
    caret: upToCaret.length,
  };
};

export const removeVariableBefore = (formula: string, caret: number) => {
  const variable = formula.slice(0, caret).match(new RegExp(`${VARIABLE.source}$`))?.[0];
  if (!variable) return null;
  const start = caret - variable.length;
  return { formula: formula.slice(0, start) + formula.slice(caret), caret: start };
};
